import axios from '../api/axios';

export interface WeaponStats {
  fireRate?: number;
  damagePerShot?: number;
  damageType?: string;
  range?: number;
  velocity?: number;
  ammoCapacity?: number;
}

export interface MissileStats {
  launchDelay?: number;
  lockTime?: number;
  damage?: number;
  speed?: number;
  lockingRange?: number;
}

export interface ShieldStats {
  shieldHealthMax?: number;
  regenRate?: number;
  downedRegenDelay?: number;
  damagedRegenDelay?: number;
}

export interface CoolerStats {
  coolingRate?: number;
}

export interface PowerPlantStats {
  powerOutput?: number;
}

export interface ArmorStats {
  damagePhysical?: number;
  damageEnergy?: number;
  damageDistortion?: number;
  damageThermal?: number;
  damageBiochemical?: number;
  damageStun?: number;
  signalInfrared?: number;
  signalElectromagnetic?: number;
  signalCrossSection?: number;
}

export interface QuantumDriveStats {
  driveSpeed?: number;
  spoolUpTime?: number;
  cooldownTime?: number;
  fuelRequirement?: number;
  calibrationTime?: number;
}

export interface EmpStats {
  chargeTime?: number;
  cooldownTime?: number;
  distortionDamage?: number;
  radius?: number;
}

export interface FuelTankStats {
  capacity?: number;
  fuelType?: string;
}

export interface FuelIntakeStats {
  intakeRate?: number;
}

export interface LifeSupportStats {
  generation?: number;
}

export interface RadarStats {
  sensitivity?: number;
  piercing?: number;
}

export interface MiningLaserStats {
  instability?: number;
  resistance?: number;
  optimalWindow?: number;
  power?: number;
  range?: number;
}

export interface SalvageStats {
  headCount?: number;
  armingDistance?: number;
  usesCargoGrid?: boolean;
}

export interface TractorBeamStats {
  range?: number;
  force?: number;
  volume?: number;
  cargoRange?: number;
  cargoForce?: number;
}

export interface QedStats {
  jammerRange?: number;
  snareRadius?: number;
  chargeTime?: number;
  dischargeTime?: number;
}

export interface TurretStats {
  yawMax?: number;
  yawMin?: number;
  pitchMax?: number;
  pitchMin?: number;
  rotationSpeed?: number;
  gunCount?: number;
}

export interface CountermeasureStats {
  ammoCount?: number;
}

// Power Pip System stats for 4.5 Dawn of Engineering
export interface PowerPipStats {
  powerUnits?: number; // Maximum pips this component can use
  minimumConsumptionFraction?: number; // Fraction of max pips required at minimum
  minPipsRequired?: number; // Calculated: ceil(minimumConsumptionFraction * powerUnits)
  powerRangeLowModifier?: number; // Performance at low power (min pips)
  powerRangeMediumModifier?: number; // Performance at medium power
  powerRangeHighModifier?: number; // Performance at max pips
}

// Represents an equipped item in a hardpoint (from loadout API)
export interface EquippedItem {
  itemName: string;
  displayName: string;
  manufacturer?: string;
  model?: string;
  size: number;
  slotName?: string;
  itemDefinition?: ItemDefinition;
}

// Represents a loaded item (like a missile) in a launcher/rack
export interface LoadedItem {
  id: number;
  slotName: string;
  itemName: string;
  size: number;
  displayName: string;
  model: string;
  manufacturer: string;
  itemDefinition?: ItemDefinition;
  childAttachments?: LoadedItem[]; // Nested attachments (e.g., mining modules on a mining laser)
  // Direct stats shortcuts (populated from itemDefinition for convenience)
  shieldStats?: ShieldStats;
  weaponStats?: WeaponStats;
  missileStats?: MissileStats;
}

export interface ItemDefinition {
  id: number;
  itemName: string;
  localizedName: string;
  description: string;
  type: string;
  subType: string;
  size: number;
  grade: number;
  manufacturer: string;
  tags: string;
  requiredTags: string;
  powerBase: number;
  powerDraw: number;
  health: number;
  // Component-specific stats
  weaponStats?: WeaponStats;
  missileStats?: MissileStats;
  shieldStats?: ShieldStats;
  coolerStats?: CoolerStats;
  powerPlantStats?: PowerPlantStats;
  armorStats?: ArmorStats;
  quantumDriveStats?: QuantumDriveStats;
  empStats?: EmpStats;
  fuelTankStats?: FuelTankStats;
  fuelIntakeStats?: FuelIntakeStats;
  lifeSupportStats?: LifeSupportStats;
  radarStats?: RadarStats;
  miningLaserStats?: MiningLaserStats;
  salvageStats?: SalvageStats;
  tractorBeamStats?: TractorBeamStats;
  qedStats?: QedStats;
  turretStats?: TurretStats;
  countermeasureStats?: CountermeasureStats;
  powerPipStats?: PowerPipStats; // 4.5 Power Pip System
  emissionStats?: EmissionStats; // Signature/detectability data
}

// Emission/Signature stats for detectability
export interface EmissionStats {
  powerToEM: number | null;
  decayRateOfEM: number | null;
  emNominalSignature: number | null;
  thermalEnergyDraw: number | null;
  thermalEnergyBase: number | null;
  temperatureToIR: number | null;
  irNominalSignature: number | null;
  maxCoolingRate: number | null;
  maxTemperature: number | null;
  overheatTemperature: number | null;
  startIRTemperature: number | null;
}

/**
 * Check if a ship is a ground vehicle (no quantum drive, limited systems)
 */
export const isGroundVehicle = (ship: Ship | null): boolean => {
  if (!ship) return false;
  const name = ship.name?.toUpperCase() || '';
  // Ground vehicles: ATLS, PTV, Ursa, Lynx, Mule, Cyclone series, ROC, SRV, HoverQuad
  return name.includes('ATLS') || 
         name.includes('PTV') || 
         name.includes('URSA') || 
         name.includes('LYNX') || 
         name.includes('MULE') || 
         name.includes('CYCLONE') || 
         name.includes('ROC') || 
         name.includes('SRV') ||
         name.includes('HOVERQUAD') ||
         name.includes('BALLISTA') ||
         name.includes('CENTURION') ||
         name.includes('SPARTAN');
};

export interface ShipHardpoint {
  id: number;
  shipId?: number;
  hardpointType: string; // High-level category
  componentType?: string | null; // Specific component type from P4K
  subtypes?: string | null; // Component subtypes
  size: number; // 0-9 for S0-S9
  quantity?: number;
  mountType?: string | null; // "Fixed", "Gimballed", "Turret", "Remote Turret"
  slotName?: string | null;
  category?: string; // From loadout API: "Weapons", "Missiles", etc.
  defaultItemName?: string | null; // Stock/default item code
  defaultItemDisplayName?: string | null; // Formatted display name
  defaultItemModel?: string | null; // Model name only
  defaultItemManufacturer?: string | null; // Manufacturer name
  equippedItem?: EquippedItem; // From loadout API: equipped item with full details
  itemDefinition?: ItemDefinition; // Detailed component stats from P4K
  loadedItems?: LoadedItem[]; // Missiles/torpedoes loaded in launchers/racks, or weapons in gimbals
  isEditable?: boolean; // Can players modify this component?
}

export interface Ship {
  id: number;
  name: string;
  slug: string;
  manufacturer: string | null;
  manufacturerCode: string | null;
  role: string | null;
  focus: string | null;
  size: string | null;
  productionStatus: string | null;
  length: number | null;
  beam: number | null;
  height: number | null;
  mass: number | null;
  crewMin: number | null;
  crewMax: number | null;
  cargoCapacity: number | null;
  maxSpeed: number | null;
  quantumRange: number | null;
  weaponHardpoints: number | null;
  shieldSize: string | null;
  priceAUEC: number | null;
  priceUSD: number | null;
  storeImageUrl: string | null;
  thumbnailUrl: string | null;
  brochureUrl: string | null;
  description: string | null;
  manufacturerDescription: string | null;
  recommendedDepartments: string | null;
  ownedByUserCount: number;
  dataSource: string | null;
  lastSynced: string;
  createdAt: string;
  updatedAt: string;
  // Fuel and hull stats from P4K data
  hullHealth: number | null;
  hydrogenFuelCapacity: number | null;
  quantumFuelCapacity: number | null;
  // Armor stats from P4K data
  armorHealth: number | null;
  armorType: string | null;
  armorPhysicalDamageMultiplier: number | null;
  armorEnergyDamageMultiplier: number | null;
  armorDistortionDamageMultiplier: number | null;
  armorSignalInfrared: number | null;
  armorSignalElectromagnetic: number | null;
  armorSignalCrossSection: number | null;
  // Signature System Cross Section (actual dimensions from P4K)
  signatureSystemCrossSectionX: number | null;
  signatureSystemCrossSectionY: number | null;
  signatureSystemCrossSectionZ: number | null;
  // Power pip system (4.5 Dawn of Engineering)
  weaponPoolSize: number | null; // Shared power pool for weapons
  // Maneuverability fields from P4K flight controller
  angularAccelDecay: number | null; // Rotation decay rate (lower = more agile)
  linearAccelDecay: number | null; // Linear acceleration decay (lower = more responsive)
  boostSpeedBackward: number | null; // Reverse boost speed in m/s
  maxSpeedLimit: number | null; // Physics maximum velocity in m/s
  // Turn rates from P4K flight controller (degrees per second)
  pitchRate: number | null; // Pitch rotation rate (nose up/down)
  yawRate: number | null; // Yaw rotation rate (nose left/right)
  rollRate: number | null; // Roll rotation rate (barrel roll)

  // Controller-derived combat turning metrics (t90 in seconds)
  // Provided as a nested object from the API (ShipsController.ControllerTurn)
  controllerTurn?: {
    combatTurnT90PitchS?: number | null;
    combatTurnT90YawS?: number | null;
    combatTurnT90RollS?: number | null;
    combatTurnT90PrimaryAimS?: number | null;
    combatTurnT90WorstAimS?: number | null;
  } | null;

  hardpoints?: ShipHardpoint[];
  hasHardpoints?: boolean; // Returned by list API for P4K badge indicator
  hasMedicalBed?: boolean; // Derived capability flag from API (P4K interior entity detection)
  totalWeaponDps?: number; // Calculated total weapon DPS from hardpoints
}

export interface ShipListResponse {
  ships: Ship[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface Manufacturer {
  code: string;
  name: string;
  shipCount: number;
}

export interface Size {
  size: string;
  shipCount: number;
}

export interface Role {
  role: string;
  shipCount: number;
}

export interface ProductionStatus {
  status: string;
  shipCount: number;
}

export interface ShipStats {
  totalShips: number;
  flightReady: number;
  inProduction: number;
  inConcept: number;
  manufacturers: number;
}

const shipBySlugCache = new Map<string, Promise<Ship>>();

export interface ShipFacility {
  key: string;
  label: string;
  status: 'yes' | 'no' | 'pending';
  count: number;
  detail?: string | null;
}

export interface ShipClass {
  value: string;
  label: string;
}

export const shipService = {
  // Get all ships with filters and pagination
  async getShips(params: {
    page?: number;
    pageSize?: number;
    manufacturer?: string;
    size?: string;
    role?: string;
    shipClass?: string;
    productionStatus?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    includeHardpoints?: boolean;
  }): Promise<ShipListResponse> {
    const cacheBuster = Date.now();
    const response = await axios.get('/ships', { params: { ...params, _t: cacheBuster } });
    return response.data;
  },

  /** CIG hull classes present in the data (value = CIG's tag, verbatim). */
  async getShipClasses(): Promise<ShipClass[]> {
    const response = await axios.get<{ classes: ShipClass[] }>('/ships/filters');
    return response.data.classes ?? [];
  },

  // Get ship by slug
  /** What the ship actually has on board. "pending" = not tracked in our
   *  data yet - different from a real no. */
  async getFacilities(slug: string): Promise<ShipFacility[]> {
    const res = await axios.get<{ facilities: ShipFacility[] }>(
      `/ships/${slug}/facilities`
    );
    return res.data.facilities;
  },

  async getShipBySlug(slug: string): Promise<Ship> {
    const key = slug.trim().toLowerCase();
    const cached = shipBySlugCache.get(key);
    if (cached) {
      return cached;
    }

    const request = axios
      .get(`/ships/${slug}`)
      .then(r => r.data as Ship)
      .catch(err => {
        shipBySlugCache.delete(key);
        throw err;
      });

    shipBySlugCache.set(key, request);
    return request;
  },

  // Get all manufacturers
  async getManufacturers(): Promise<Manufacturer[]> {
    const response = await axios.get('/ships/manufacturers');
    return response.data;
  },

  // Get all sizes
  async getSizes(): Promise<Size[]> {
    const response = await axios.get('/ships/sizes');
    return response.data;
  },

  // Get all roles
  async getRoles(): Promise<Role[]> {
    const response = await axios.get('/ships/roles');
    return response.data;
  },

  // Get production statuses
  async getProductionStatuses(): Promise<ProductionStatus[]> {
    const response = await axios.get('/ships/production-statuses');
    return response.data;
  },

  // Get ship stats
  async getStats(): Promise<ShipStats> {
    const response = await axios.get('/ships/stats');
    return response.data;
  },

  // Get Star Citizen version info
  async getVersion(): Promise<{ scVersion: string; dataSource: string; lastUpdated: string }> {
    try {
      const response = await axios.get('/ships/version');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch version:', error);
      return { scVersion: 'Alpha', dataSource: 'LIVE', lastUpdated: '' };
    }
  }
};
